import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress, Modal, Typography } from '@mui/material';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { apiService } from '../../services/api';

const SmartRagMigrationManager: React.FC = () => {
  const { account, refreshUserDetails } = useAccount();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [needsKmsMigration, setNeedsKmsMigration] = useState(false);
  const [kmsMigrationStatus, setKmsMigrationStatus] = useState<
    'idle' | 'migrating' | 'error' | 'completed'
  >('idle');
  const [kmsMigrationError, setKmsMigrationError] = useState('');

  const [isMigratingSmartRag, setIsMigratingSmartRag] = useState(false);

  const smartRagStatus = account?.smartRagMigrationStatus;
  const failedMigrations = account?.failedMigrations || [];

  const needsSmartRagMigration =
    smartRagStatus === 'pending' || smartRagStatus === 'error' || smartRagStatus === 'migrating';
  const showFailedSummary = smartRagStatus === 'completed' && failedMigrations.length > 0;

  // 1. Check if KMS Migration is needed
  useEffect(() => {
    const checkKmsMigration = async () => {
      if (!account?.uid) {
        return;
      }
      if (account.kmsMigratedAt) {
        return;
      }

      try {
        const { httpClient } = await import('../../services/http/httpClient');
        const [libResponse, fsResponse] = await Promise.all([
          httpClient
            .post<{ success: boolean; needsMigration?: boolean }>('/migrateLibraryToKms', {
              clientId: account.clientId,
              teamId: account.teamId,
              checkOnly: true,
            })
            .catch(() => ({ success: false, needsMigration: false })),
          httpClient
            .post<{ success: boolean; needsMigration?: boolean }>('/migrateFirestoreData', {
              clientId: account.clientId,
              checkOnly: true,
            })
            .catch(() => ({ success: false, needsMigration: false })),
        ]);

        if (libResponse.needsMigration || fsResponse.needsMigration) {
          setNeedsKmsMigration(true);
        } else if (libResponse.success && fsResponse.success) {
          // If both checks succeeded and neither needs migration, update the user record to skip future checks
          await apiService.updateAccountDetails({ kmsMigratedAt: new Date().toISOString() });
        }
      } catch (_error) {
        // ignore
      }
    };

    checkKmsMigration();
  }, [account?.uid, account?.clientId, account?.teamId, account?.kmsMigratedAt]);

  // 2. Sync Smart RAG state
  useEffect(() => {
    if (smartRagStatus === 'migrating') {
      setIsMigratingSmartRag(true);
    } else {
      setIsMigratingSmartRag(false);
    }
  }, [smartRagStatus]);

  const isVisible = needsKmsMigration || needsSmartRagMigration || showFailedSummary;
  const isMigratingAny = kmsMigrationStatus === 'migrating' || isMigratingSmartRag;

  const handleAcknowledge = async () => {
    try {
      if (showFailedSummary) {
        await apiService.updateAccountDetails({
          client: {
            failedMigrations: [],
          },
        });
      }
      await refreshUserDetails();

      // After acknowledging, if we still need KMS migration, we do nothing and let the modal stay open on the upgrade screen.
      // If KMS migration is also done, the modal will naturally close because isVisible becomes false.
    } catch (_e) {
      showSnackbar('Failed to acknowledge migration. Please try again.', 'error');
    }
  };

  const executeKmsMigration = async (): Promise<boolean> => {
    if (!account?.clientId || !account?.uid) {
      return true; // Pretend success so it continues
    }

    setKmsMigrationStatus('migrating');
    setKmsMigrationError('');
    try {
      const storageService = (await import('../../services/storageService')).default;
      const { exportKey } = await import('../../services/encryptionService');
      const mdk = storageService.getEncryptionKey();
      if (!mdk) {
        setKmsMigrationError(
          'Please log out and log back in to unlock your encryption key before upgrading.'
        );
        setKmsMigrationStatus('error');
        return false;
      }

      const legacyKeyBase64 = await exportKey(mdk);

      const { httpClient } = await import('../../services/http/httpClient');

      // 1. Migrate physical files
      const response = await httpClient.post<{
        success: boolean;
        message?: string;
        error?: string;
        failedCount?: number;
      }>(
        '/migrateLibraryToKms',
        {
          clientId: account.clientId,
          teamId: account.teamId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-encryption-key': legacyKeyBase64,
          },
        }
      );

      if (
        response &&
        typeof response === 'object' &&
        ('success' in response || 'message' in response)
      ) {
        if (response.failedCount && response.failedCount > 0) {
          const confirmDiscard = window.confirm(
            `Migration encountered issues: ${response.message}\n\nThis usually happens if your encryption keys were reset, making some old files irretrievable. Would you like to complete the migration anyway and mark the unreadable files as irretrievable?`
          );
          if (confirmDiscard) {
            const retryResponse = await httpClient.post<{
              success: boolean;
              message?: string;
              error?: string;
            }>(
              '/migrateLibraryToKms',
              {
                clientId: account.clientId,
                teamId: account.teamId,
                forceComplete: true,
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'x-encryption-key': legacyKeyBase64,
                },
              }
            );
            if (!retryResponse.success) {
              throw new Error(retryResponse.error || 'Forced migration failed');
            }
          } else {
            setKmsMigrationError(
              'Migration paused. You must complete it to continue using the platform.'
            );
            setKmsMigrationStatus('error');
            return false;
          }
        }
      }
      await httpClient.post<{ success: boolean; message?: string }>(
        '/migrateFirestoreData',
        {
          clientId: account.clientId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-encryption-key': legacyKeyBase64,
          },
        }
      );

      await apiService.updateAccountDetails({ kmsMigratedAt: new Date().toISOString() });
      setNeedsKmsMigration(false);
      setKmsMigrationStatus('completed');
      return true;
    } catch (error) {
      setKmsMigrationError(`Security upgrade failed: ${(error as Error).message}`);
      setKmsMigrationStatus('error');
      return false;
    }
  };

  const executeSmartRagMigration = async (): Promise<boolean> => {
    if (!account?.clientId) {
      return true;
    }
    setIsMigratingSmartRag(true);
    try {
      const res = await apiService.migrateSmartRag({ client_id: account.clientId });
      if (res.success || res.data?.status === 'success') {
        if (account.storageType === 'sharepoint') {
          showSnackbar(
            'Please trigger a manual sync via the Knowledge Dashboard to finish your upgrade.',
            'info'
          );
          navigate('/admin/knowledge_base');
        } else {
          showSnackbar('Knowledge Base upgraded successfully!', 'success');
        }
        await refreshUserDetails();
        return true;
      } else {
        showSnackbar(res.message || 'Upgrade failed. Please try again.', 'error');
        setIsMigratingSmartRag(false);
        return false;
      }
    } catch (_error) {
      showSnackbar('An error occurred during the upgrade. Please try again.', 'error');
      setIsMigratingSmartRag(false);
      return false;
    }
  };

  const handleUpgradeClick = async () => {
    let kmsSuccess = true;
    if (needsKmsMigration) {
      kmsSuccess = await executeKmsMigration();
    }

    if (kmsSuccess && needsSmartRagMigration) {
      await executeSmartRagMigration();
    } else if (kmsSuccess) {
      await refreshUserDetails();
      showSnackbar('Security Upgrade completed successfully!', 'success');
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Modal open={true} disableEscapeKeyDown>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: showFailedSummary ? 600 : 500 },
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 5,
          borderRadius: 2,
          textAlign: 'center',
          outline: 'none',
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2, mx: 'auto' }} />

        {showFailedSummary && !needsKmsMigration && !needsSmartRagMigration ? (
          <>
            <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
              Upgrade Complete
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Your Knowledge Base has been successfully upgraded to the new Smart RAG architecture.
              However, the following files were heavily encrypted or corrupted and could not be
              migrated, so they were deleted from your library.
            </Typography>
            <Box
              sx={{
                maxHeight: 200,
                overflowY: 'auto',
                mb: 3,
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                textAlign: 'left',
              }}
            >
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {failedMigrations.map((file) => (
                  <li key={file}>
                    <Typography variant="body2" color="error">
                      {file}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={handleAcknowledge}
              sx={{
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderRadius: 8,
              }}
            >
              Acknowledge & Continue
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
              BIDLOGIQ is Upgrading!
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              We are rolling out a major security and file organisation upgrade. This upgrade
              transitions your account to our new Google Cloud KMS envelope encryption standard and
              prepares your library for advanced AI processing.
              {account?.storageType === 'sharepoint'
                ? ' Please re-sync your SharePoint folder to complete the upgrade.'
                : ''}
            </Typography>

            {isMigratingAny ? (
              <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <CircularProgress size={48} sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {kmsMigrationStatus === 'migrating'
                    ? 'Upgrading security encryption. This might take a minute...'
                    : 'Upgrading your Knowledge Base. This might take a minute...'}
                </Typography>
              </Box>
            ) : (
              <Button
                variant="contained"
                size="large"
                onClick={handleUpgradeClick}
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderRadius: 8,
                }}
              >
                {account?.storageType === 'sharepoint' && !needsKmsMigration
                  ? 'Re-Sync SharePoint'
                  : 'Upgrade My Account'}
              </Button>
            )}

            {kmsMigrationStatus === 'error' && !isMigratingAny && (
              <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                {kmsMigrationError ||
                  'The security upgrade attempt failed. Please try again or contact support.'}
              </Typography>
            )}

            {smartRagStatus === 'error' && !isMigratingAny && kmsMigrationStatus !== 'error' && (
              <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                The knowledge base upgrade attempt failed. Please try again or contact support.
              </Typography>
            )}
          </>
        )}
      </Box>
    </Modal>
  );
};

export default SmartRagMigrationManager;
